import Phaser from 'phaser';
import type { Socket } from 'socket.io-client';
import {
  SocketEvents,
  SPRITE_KEYS,
  type BattleEndPayload,
  type BattleForceSwitchPayload,
  type BattleParticipantSnapshot,
  type BattleStartPayload,
  type BattleUpdatePayload,
  type BattleWaitingPayload,
  type EncounterStartPayload,
  type ErrorPayload,
  type InventoryEntryPayload,
  type PartyMemberPayload,
  type PvpDisconnectedPayload,
} from '@pokemiu/shared';

/** Init data shape: scene boots from either a wild encounter or a PvP start. */
export type BattleSceneData = { encounter: EncounterStartPayload } | { start: BattleStartPayload };

/**
 * BattleScene (M4.1 → M4.4) — Gen-1 style wild battle UI.
 *
 *   ┌──────────────────────────┐
 *   │ wild HP/level/name       │  wild front sprite
 *   │ (top-left info box)      │  (top-right)
 *   │                          │
 *   │  player back sprite      │  player HP/level/name
 *   │  (mid-left)              │  (mid-right info box)
 *   ├──────────────────────────┤
 *   │ message line             │  bottom dialog
 *   │ ► Attack  Switch         │  main menu (2x2: Attack/Switch/Bag/Run)
 *   │ ► Bag     Run            │
 *   └──────────────────────────┘
 *
 * Server-authoritative: all action submissions go through `battle:action`
 * and the server replies with `battle:update` (turn events) and finally
 * `battle:end`. This scene never decides outcomes locally.
 *
 * Force-switch (M4.4): on `battle:forceswitch`, opens a modal party picker
 * with a 30 s countdown driven by the server-supplied `deadline`. If the
 * player doesn't pick in time, the server auto-picks and emits a regular
 * `battle:update` carrying `playerSnapshot` for the new active — the client
 * dismisses the modal on receiving that.
 */
export class BattleScene extends Phaser.Scene {
  private socket!: Socket;
  private encounter!: EncounterStartPayload;

  // Sprites
  private wildSprite!: Phaser.GameObjects.Image;
  private playerSprite!: Phaser.GameObjects.Image;

  // Info panels
  private wildInfo!: Phaser.GameObjects.Text;
  private playerInfo!: Phaser.GameObjects.Text;
  private wildHpBg!: Phaser.GameObjects.Rectangle;
  private wildHpFg!: Phaser.GameObjects.Rectangle;
  private playerHpBg!: Phaser.GameObjects.Rectangle;
  private playerHpFg!: Phaser.GameObjects.Rectangle;

  // Bottom dialog
  private dialogBg!: Phaser.GameObjects.Rectangle;
  private dialogText!: Phaser.GameObjects.Text;
  private menuItems: Phaser.GameObjects.Text[] = [];
  private menuCursor!: Phaser.GameObjects.Text;

  // Force-switch modal
  private forceSwitchOverlay: Phaser.GameObjects.GameObject[] = [];
  private forceSwitchCountdown: Phaser.GameObjects.Text | null = null;
  private forceSwitchDeadline = 0;
  private forceSwitchTimerEvent: Phaser.Time.TimerEvent | null = null;
  private forceSwitchEligibleIds: ReadonlyArray<string> = [];

  // State
  private currentMenu: 'main' | 'attack' | 'bag' | 'switch' | 'forceswitch' | 'message' = 'message';
  private menuIndex = 0;
  private mainOptions: ReadonlyArray<string> = ['Attack', 'Switch', 'Bag', 'Run'];
  private currentPlayer: BattleParticipantSnapshot = null as unknown as BattleParticipantSnapshot;
  private currentPlayerHp = 0;
  private currentWildHp = 0;
  private playerMaxHp = 0;
  private wildMaxHp = 0;
  private inventory: InventoryEntryPayload[] = [];
  private party: PartyMemberPayload[] = [];
  /** ID-keyed map for fast switch-target lookups. Mutated in-place on swap. */
  private partyById: Map<string, PartyMemberPayload> = new Map();
  private keyHandler!: (e: KeyboardEvent) => void;
  private menuOpenedAt = 0;
  private endHandled = false;
  private submitWatchdog: Phaser.Time.TimerEvent | null = null;

  // PvP-specific state
  private mode: 'wild' | 'pvp' = 'wild';
  private opponentUsername: string | null = null;
  private battleId = '';
  private localPlayerId = '';
  private playerSpeed = 0;
  private opponentSpeed = 0;
  /** PvP-only: persistent speed-advantage label on the faster side. */
  private pvpSpeedLabel: Phaser.GameObjects.Text | null = null;
  /** Active opponent (right side) snapshot — wild creature or PvP opponent. */
  private opponentSnap: BattleParticipantSnapshot | null = null;
  // Waiting / disconnect overlays
  private waitingOverlay: Phaser.GameObjects.GameObject[] = [];
  private waitingTimerEvent: Phaser.Time.TimerEvent | null = null;
  private waitingDeadline = 0;
  private disconnectOverlay: Phaser.GameObjects.GameObject[] = [];
  private disconnectTimerEvent: Phaser.Time.TimerEvent | null = null;
  private disconnectDeadline = 0;

  constructor() {
    super({ key: 'BattleScene' });
  }

  init(data: BattleSceneData): void {
    if ('start' in data) {
      // PvP path.
      const s = data.start;
      this.mode = 'pvp';
      this.opponentUsername = s.opponentUsername;
      this.battleId = s.battleId;
      this.currentPlayer = s.player;
      this.opponentSnap = s.opponent;
      this.currentPlayerHp = s.player.currentHp;
      this.currentWildHp = s.opponent.currentHp;
      this.playerMaxHp = s.player.maxHp;
      this.wildMaxHp = s.opponent.maxHp;
      this.inventory = [...s.playerInventory];
      this.party = [...s.playerParty];
      // Construct a stub EncounterStartPayload for legacy fields used downstream
      // (sprite key lookup, battleId reads). The actual `wild` slot is the
      // opponent.
      this.encounter = {
        battleId: s.battleId,
        player: s.player,
        wild: s.opponent,
        playerInventory: s.playerInventory,
        playerParty: s.playerParty,
      };
      // No Run option in PvP per design (server rejects with pvp_no_run).
      this.mainOptions = ['Attack', 'Switch', 'Bag'];
      this.playerSpeed = s.player.speed;
      this.opponentSpeed = s.opponent.speed;
    } else {
      const e = data.encounter;
      this.mode = 'wild';
      this.opponentUsername = null;
      this.battleId = e.battleId;
      this.encounter = e;
      this.currentPlayer = e.player;
      this.opponentSnap = e.wild;
      this.currentPlayerHp = e.player.currentHp;
      this.currentWildHp = e.wild.currentHp;
      this.playerMaxHp = e.player.maxHp;
      this.wildMaxHp = e.wild.maxHp;
      this.inventory = [...(e.playerInventory ?? [])];
      this.party = [...(e.playerParty ?? [])];
      this.mainOptions = ['Attack', 'Switch', 'Bag', 'Run'];
      this.playerSpeed = e.player.speed;
      this.opponentSpeed = e.wild.speed;
    }
    this.partyById = new Map(this.party.map((m) => [m.creatureId, m]));
    this.endHandled = false;
    this.forceSwitchEligibleIds = [];
    this.forceSwitchDeadline = 0;
    this.waitingDeadline = 0;
    this.disconnectDeadline = 0;
  }

  create(): void {
    this.socket = this.game.registry.get('socket') as Socket;
    const jwtPayload = this.game.registry.get('jwtPayload') as { sub: string } | undefined;
    this.localPlayerId = jwtPayload?.sub ?? '';
    const W = this.cameras.main.width;
    const H = this.cameras.main.height;

    this.add.rectangle(0, 0, W, H, 0x86c98c).setOrigin(0, 0);
    this.add.rectangle(0, H - 56, W, 56, 0x202020).setOrigin(0, 0);
    this.add.rectangle(0, H - 56, W, 1, 0xffffff).setOrigin(0, 0);

    // ---------- Opponent creature (top-right) — wild creature or PvP foe. ----
    const opp = this.opponentSnap ?? this.encounter.wild;
    const oppKey = SPRITE_KEYS.creatures.front(opp.speciesId);
    this.wildSprite = this.add.image(W - 60, 56, oppKey).setOrigin(0.5, 0.5);

    this.wildInfo = this.add
      .text(8, 8, this.formatInfo(opp), {
        fontFamily: 'monospace',
        fontSize: '9px',
        color: '#ffffff',
      })
      .setOrigin(0, 0);
    this.wildHpBg = this.add.rectangle(8, 30, 80, 5, 0x000000, 0.7).setOrigin(0, 0.5);
    this.wildHpFg = this.add.rectangle(8, 30, 80, 4, 0x66dd66).setOrigin(0, 0.5);
    this.refreshHp(this.wildHpFg, this.currentWildHp, this.wildMaxHp);

    if (this.mode === 'pvp' && this.opponentUsername) {
      // Tiny tag above the info row identifying the trainer behind the creature.
      this.add
        .text(8, 0, this.opponentUsername.toUpperCase(), {
          fontFamily: 'monospace',
          fontSize: '8px',
          color: '#ffe066',
        })
        .setOrigin(0, 0);
      // Persistent speed-order indicator — updates on every creature swap.
      this.pvpSpeedLabel = this.add
        .text(W / 2, 0, '', {
          fontFamily: 'monospace',
          fontSize: '8px',
          color: '#ffe066',
        })
        .setOrigin(0.5, 0);
      this.refreshPvpSpeedLabel();
    }

    // ---------- Player creature (mid-left) ----------
    const playerKey = SPRITE_KEYS.creatures.back(this.encounter.player.speciesId);
    this.playerSprite = this.add.image(60, 130, playerKey).setOrigin(0.5, 0.5);

    this.playerInfo = this.add
      .text(W - 8, 100, this.formatInfo(this.encounter.player), {
        fontFamily: 'monospace',
        fontSize: '9px',
        color: '#ffffff',
      })
      .setOrigin(1, 0);
    this.playerHpBg = this.add.rectangle(W - 8, 122, 80, 5, 0x000000, 0.7).setOrigin(1, 0.5);
    this.playerHpFg = this.add.rectangle(W - 8, 122, 80, 4, 0x66dd66).setOrigin(1, 0.5);
    this.refreshHp(this.playerHpFg, this.currentPlayerHp, this.playerMaxHp);

    // ---------- Dialog + menu (bottom) ----------
    this.dialogBg = this.add.rectangle(0, H - 56, W, 56, 0x000000, 0).setOrigin(0, 0);
    this.dialogText = this.add
      .text(8, H - 50, '', {
        fontFamily: 'monospace',
        fontSize: '10px',
        color: '#ffffff',
        wordWrap: { width: W - 16 },
      })
      .setOrigin(0, 0);
    this.menuCursor = this.add
      .text(0, 0, '►', {
        fontFamily: 'monospace',
        fontSize: '10px',
        color: '#ffe066',
      })
      .setOrigin(0, 0)
      .setVisible(false);

    this.wireSocketEvents();
    this.bindInput();

    const opening =
      this.mode === 'pvp' && this.opponentUsername
        ? `${this.opponentUsername} sent out ${opp.speciesDisplayName}!`
        : `Wild ${opp.speciesDisplayName} appeared!`;
    this.dialogText.setText(opening);
    this.time.delayedCall(900, () => this.openMainMenu());
  }

  // -------------------- HP / info --------------------

  private formatInfo(p: { speciesDisplayName: string; level: number; speed: number }): string {
    return `${p.speciesDisplayName.toUpperCase()}  Lv.${p.level}  SPD:${p.speed}`;
  }

  /** Redraw the PvP speed-advantage indicator based on current active speeds. */
  private refreshPvpSpeedLabel(): void {
    if (!this.pvpSpeedLabel) return;
    if (this.playerSpeed > this.opponentSpeed) {
      this.pvpSpeedLabel.setText('⚡ You go first!').setColor('#ffe066');
    } else if (this.opponentSpeed > this.playerSpeed) {
      this.pvpSpeedLabel.setText('⚡ Opponent goes first!').setColor('#ff8866');
    } else {
      this.pvpSpeedLabel.setText('⚡ Speed tied — random!').setColor('#aaaaaa');
    }
  }

  private refreshHp(bar: Phaser.GameObjects.Rectangle, cur: number, max: number): void {
    const ratio = Math.max(0, Math.min(1, cur / max));
    const fullW = 80;
    bar.width = fullW * ratio;
    bar.fillColor = this.hpColor(cur, max);
  }

  private hpColor(cur: number, max: number): number {
    if (cur <= 0) return 0xaa6666;
    const pct = cur / max;
    if (pct > 0.5) return 0x66dd66;
    if (pct > 0.2) return 0xffcc33;
    return 0xdd6666;
  }

  private tweenHp(
    bar: Phaser.GameObjects.Rectangle,
    fromHp: number,
    toHp: number,
    max: number,
  ): Promise<void> {
    return new Promise((resolve) => {
      const obj = { v: fromHp };
      this.tweens.add({
        targets: obj,
        v: toHp,
        duration: 350,
        onUpdate: () => this.refreshHp(bar, obj.v, max),
        onComplete: () => {
          this.refreshHp(bar, toHp, max);
          resolve();
        },
      });
    });
  }

  // -------------------- Menus --------------------

  private openMainMenu(): void {
    this.currentMenu = 'main';
    this.menuIndex = 0;
    this.dialogText.setText('What will you do?');
    this.renderMenu(this.mainOptions);
  }

  private openAttackMenu(): void {
    this.currentMenu = 'attack';
    this.menuIndex = 0;
    this.menuOpenedAt = Date.now();
    this.dialogText.setText('Choose a move:');
    const labels = this.currentPlayer.moves.map((m) => m.displayName.toUpperCase());
    this.renderMenu(labels);
  }

  private openBagMenu(): void {
    const usable = this.inventory.filter((i) => i.quantity > 0);
    if (usable.length === 0) {
      this.flashMessage('Bag is empty!');
      return;
    }
    this.currentMenu = 'bag';
    this.menuIndex = 0;
    this.menuOpenedAt = Date.now();
    this.dialogText.setText('Use which item?');
    const labels = usable.map((i) => `${i.displayName.toUpperCase()} x${i.quantity}`);
    this.renderMenu(labels);
  }

  /**
   * Voluntary switch: lists every party member with HP / Lv. Active is shown
   * in grey + "(IN)" suffix; fainted members are shown in dim red. Selecting
   * either is a no-op (cancelMenu returns to main); only valid targets emit.
   */
  private openSwitchMenu(): void {
    const switchable = this.party.filter(
      (m) => !m.fainted && m.creatureId !== this.currentPlayer.creatureId,
    );
    if (switchable.length === 0) {
      this.flashMessage('No one else to send out!');
      return;
    }
    this.currentMenu = 'switch';
    this.menuIndex = 0;
    this.menuOpenedAt = Date.now();
    this.dialogText.setText('Send which one?');
    const labels = switchable.map(
      (m) => `${m.speciesDisplayName.toUpperCase()} L${m.level} ${m.currentHp}/${m.maxHp}`,
    );
    this.renderMenu(labels);
  }

  private renderMenu(options: ReadonlyArray<string>): void {
    this.clearMenu();
    const W = this.cameras.main.width;
    const H = this.cameras.main.height;
    // 2-column grid for compact layout (works for 4 main options or moves).
    const cols = options.length <= 2 ? options.length : 2;
    const colW = W / cols;
    options.forEach((opt, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = 16 + col * colW;
      const y = H - 24 + row * 12;
      const t = this.add
        .text(x, y, opt, {
          fontFamily: 'monospace',
          fontSize: '10px',
          color: '#ffffff',
        })
        .setOrigin(0, 0);
      this.menuItems.push(t);
    });
    this.menuCursor.setVisible(true);
    this.placeCursor();
  }

  private clearMenu(): void {
    for (const m of this.menuItems) m.destroy();
    this.menuItems = [];
    this.menuCursor.setVisible(false);
  }

  private placeCursor(): void {
    const item = this.menuItems[this.menuIndex];
    if (!item) return;
    this.menuCursor.setPosition(item.x - 10, item.y);
  }

  // -------------------- Force-switch modal --------------------

  /**
   * Show a centred party-picker overlay above the existing battle UI.
   * Disables the main menu; only valid eligible IDs can be confirmed.
   * The countdown text refreshes every 250 ms based on `deadline`.
   */
  private openForceSwitchModal(payload: BattleForceSwitchPayload): void {
    this.forceSwitchEligibleIds = payload.eligibleCreatureIds;
    this.forceSwitchDeadline = payload.deadline;
    this.currentMenu = 'forceswitch';
    this.menuIndex = 0;
    this.clearMenu();

    const W = this.cameras.main.width;
    const H = this.cameras.main.height;

    const dim = this.add.rectangle(0, 0, W, H, 0x000000, 0.55).setOrigin(0, 0).setDepth(50);
    const panelH = 110;
    const panelW = W - 32;
    const panel = this.add
      .rectangle(16, (H - panelH) / 2, panelW, panelH, 0x1a1a2e, 0.95)
      .setOrigin(0, 0)
      .setStrokeStyle(1, 0xffe066)
      .setDepth(51);
    const title = this.add
      .text(W / 2, (H - panelH) / 2 + 6, 'Choose your next pokémon!', {
        fontFamily: 'monospace',
        fontSize: '10px',
        color: '#ffe066',
      })
      .setOrigin(0.5, 0)
      .setDepth(52);

    this.forceSwitchOverlay = [dim, panel, title];

    // List eligible members vertically (max 6 — fits 110 px panel at 12 px/row).
    const eligible = payload.eligibleCreatureIds
      .map((id) => this.partyById.get(id))
      .filter((m): m is PartyMemberPayload => Boolean(m));

    eligible.forEach((m, i) => {
      const label = `${m.speciesDisplayName.toUpperCase()} L${m.level} ${m.currentHp}/${m.maxHp}`;
      const t = this.add
        .text(28, (H - panelH) / 2 + 22 + i * 11, label, {
          fontFamily: 'monospace',
          fontSize: '9px',
          color: '#ffffff',
        })
        .setOrigin(0, 0)
        .setDepth(52);
      this.menuItems.push(t);
      this.forceSwitchOverlay.push(t);
    });

    this.forceSwitchCountdown = this.add
      .text(W - 24, (H - panelH) / 2 + 6, '', {
        fontFamily: 'monospace',
        fontSize: '9px',
        color: '#ffaa66',
      })
      .setOrigin(1, 0)
      .setDepth(52);
    this.forceSwitchOverlay.push(this.forceSwitchCountdown);

    this.menuCursor.setDepth(53).setVisible(true);
    this.placeCursor();
    this.refreshForceSwitchCountdown();
    this.forceSwitchTimerEvent?.remove(false);
    this.forceSwitchTimerEvent = this.time.addEvent({
      delay: 250,
      loop: true,
      callback: () => this.refreshForceSwitchCountdown(),
    });
  }

  private refreshForceSwitchCountdown(): void {
    if (!this.forceSwitchCountdown) return;
    const remaining = Math.max(0, Math.ceil((this.forceSwitchDeadline - Date.now()) / 1000));
    this.forceSwitchCountdown.setText(`${remaining}s`);
  }

  private closeForceSwitchModal(): void {
    for (const obj of this.forceSwitchOverlay) obj.destroy();
    this.forceSwitchOverlay = [];
    this.forceSwitchCountdown = null;
    this.forceSwitchTimerEvent?.remove(false);
    this.forceSwitchTimerEvent = null;
    this.clearMenu();
  }

  // -------------------- Input --------------------

  private bindInput(): void {
    this.keyHandler = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowUp':
        case 'w':
        case 'W':
          this.moveCursor(-2);
          break;
        case 'ArrowDown':
        case 's':
        case 'S':
          this.moveCursor(2);
          break;
        case 'ArrowLeft':
        case 'a':
        case 'A':
          this.moveCursor(-1);
          break;
        case 'ArrowRight':
        case 'd':
        case 'D':
          this.moveCursor(1);
          break;
        case 'Enter':
        case ' ':
        case 'z':
        case 'Z':
          this.confirmMenu();
          break;
        case 'Escape':
        case 'x':
        case 'X':
          this.cancelMenu();
          break;
      }
    };
    window.addEventListener('keydown', this.keyHandler);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      window.removeEventListener('keydown', this.keyHandler);
    });
  }

  private moveCursor(delta: number): void {
    if (this.menuItems.length === 0) return;
    // Force-switch modal lays items vertically; up/down should be ±1 too.
    if (this.currentMenu === 'forceswitch') {
      delta = Math.sign(delta);
      if (delta === 0) return;
    }
    this.menuIndex = (this.menuIndex + delta + this.menuItems.length) % this.menuItems.length;
    this.placeCursor();
  }

  private confirmMenu(): void {
    // Ignore confirm if a submenu just opened (prevents double-fire from key repeat).
    if (
      (this.currentMenu === 'attack' ||
        this.currentMenu === 'bag' ||
        this.currentMenu === 'switch') &&
      Date.now() - this.menuOpenedAt < 200
    )
      return;
    if (this.currentMenu === 'main') {
      const choice = this.mainOptions[this.menuIndex];
      if (choice === 'Attack') this.openAttackMenu();
      else if (choice === 'Switch') this.openSwitchMenu();
      else if (choice === 'Bag') this.openBagMenu();
      else if (choice === 'Run') this.submitAction({ type: 'run' });
    } else if (this.currentMenu === 'attack') {
      const move = this.currentPlayer.moves[this.menuIndex];
      if (move) this.submitAction({ type: 'attack', moveId: move.id });
    } else if (this.currentMenu === 'bag') {
      const usable = this.inventory.filter((i) => i.quantity > 0);
      const item = usable[this.menuIndex];
      if (item) this.submitAction({ type: 'bag', itemId: item.itemId });
    } else if (this.currentMenu === 'switch') {
      const switchable = this.party.filter(
        (m) => !m.fainted && m.creatureId !== this.currentPlayer.creatureId,
      );
      const target = switchable[this.menuIndex];
      if (target) this.submitAction({ type: 'switch', targetCreatureId: target.creatureId });
    } else if (this.currentMenu === 'forceswitch') {
      const targetId = this.forceSwitchEligibleIds[this.menuIndex];
      if (targetId) this.submitAction({ type: 'switch', targetCreatureId: targetId });
    } else if (this.currentMenu === 'message' && this.endHandled) {
      this.exitToOverworld();
    }
  }

  private cancelMenu(): void {
    if (
      this.currentMenu === 'attack' ||
      this.currentMenu === 'bag' ||
      this.currentMenu === 'switch'
    )
      this.openMainMenu();
    // Force-switch modal cannot be escaped — server requires a pick.
  }

  private flashMessage(text: string): void {
    const prev = this.dialogText.text;
    this.dialogText.setText(text);
    this.time.delayedCall(900, () => this.dialogText.setText(prev));
  }

  private submitAction(
    action:
      | { type: 'attack'; moveId: string }
      | { type: 'run' }
      | { type: 'bag'; itemId: string }
      | { type: 'switch'; targetCreatureId: string },
  ): void {
    this.clearMenu();
    this.currentMenu = 'message';
    this.dialogText.setText('...');
    this.socket.emit(SocketEvents.BATTLE_ACTION, {
      battleId: this.encounter.battleId,
      action,
    });
    this.submitWatchdog?.remove(false);
    this.submitWatchdog = this.time.delayedCall(8000, () => {
      console.warn('[BattleScene] submit watchdog fired — re-opening menu');
      if (!this.endHandled) {
        this.dialogText.setText('Server slow — try again.');
        this.openMainMenu();
      }
    });
  }

  // -------------------- Server events --------------------

  private wireSocketEvents(): void {
    this.socket.on(SocketEvents.BATTLE_UPDATE, this.handleUpdate);
    this.socket.on(SocketEvents.BATTLE_END, this.handleEnd);
    this.socket.on(SocketEvents.BATTLE_FORCESWITCH, this.handleForceSwitch);
    this.socket.on(SocketEvents.BATTLE_WAITING, this.handleWaiting);
    this.socket.on(SocketEvents.PVP_DISCONNECTED, this.handlePvpDisconnected);
    this.socket.on(SocketEvents.ERROR, this.handleError);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.socket.off(SocketEvents.BATTLE_UPDATE, this.handleUpdate);
      this.socket.off(SocketEvents.BATTLE_END, this.handleEnd);
      this.socket.off(SocketEvents.BATTLE_FORCESWITCH, this.handleForceSwitch);
      this.socket.off(SocketEvents.BATTLE_WAITING, this.handleWaiting);
      this.socket.off(SocketEvents.PVP_DISCONNECTED, this.handlePvpDisconnected);
      this.socket.off(SocketEvents.ERROR, this.handleError);
    });
  }

  /**
   * Differentiate two cases:
   *   - I submitted first (`submittedBy === localPlayerId`): my menu is
   *     already gone, show the "waiting for opponent" overlay + countdown.
   *   - Opponent submitted first: my menu is still up, show a "hurry up"
   *     hint in the dialog text without blocking the menu.
   */
  private handleWaiting = (p: BattleWaitingPayload): void => {
    if (p.battleId !== this.battleId) return;
    this.submitWatchdog?.remove(false);
    this.submitWatchdog = null;
    if (p.submittedBy === this.localPlayerId) {
      // I already chose — lock and wait for opponent.
      this.openWaitingOverlay(p.deadline);
    } else {
      // Opponent chose first — prompt me to pick without blocking the menu.
      this.openOpponentReadyHint(p.deadline);
    }
  };

  private openWaitingOverlay(deadline: number): void {
    this.closeWaitingOverlay();
    this.waitingDeadline = deadline;
    const W = this.cameras.main.width;
    const H = this.cameras.main.height;
    const dim = this.add
      .rectangle(0, H - 56, W, 56, 0x000000, 0.55)
      .setOrigin(0, 0)
      .setDepth(40);
    const label = this.add
      .text(W / 2, H - 36, 'Waiting for opponent...', {
        fontFamily: 'monospace',
        fontSize: '10px',
        color: '#ffe066',
      })
      .setOrigin(0.5, 0)
      .setDepth(41);
    const countdown = this.add
      .text(W / 2, H - 20, '', {
        fontFamily: 'monospace',
        fontSize: '9px',
        color: '#ffaa66',
      })
      .setOrigin(0.5, 0)
      .setDepth(41);
    this.waitingOverlay = [dim, label, countdown];
    const refresh = (): void => {
      const remaining = Math.max(0, Math.ceil((this.waitingDeadline - Date.now()) / 1000));
      countdown.setText(`${remaining}s`);
    };
    refresh();
    this.waitingTimerEvent?.remove(false);
    this.waitingTimerEvent = this.time.addEvent({
      delay: 250,
      loop: true,
      callback: refresh,
    });
  }

  private closeWaitingOverlay(): void {
    for (const obj of this.waitingOverlay) obj.destroy();
    this.waitingOverlay = [];
    this.waitingTimerEvent?.remove(false);
    this.waitingTimerEvent = null;
  }

  /**
   * Opponent submitted first — keep the action menu active but show a
   * countdown hint in the dialog text so the player knows to hurry.
   * Reuses `waitingTimerEvent` so `closeWaitingOverlay` (called on
   * BATTLE_UPDATE) cleans this up automatically.
   */
  private openOpponentReadyHint(deadline: number): void {
    this.closeWaitingOverlay();
    this.waitingDeadline = deadline;
    const refresh = (): void => {
      if (this.currentMenu === 'message') return; // player already submitted
      const remaining = Math.max(0, Math.ceil((this.waitingDeadline - Date.now()) / 1000));
      this.dialogText.setText(`Opponent chose! Your move! (${remaining}s)`);
    };
    refresh();
    this.waitingTimerEvent = this.time.addEvent({ delay: 250, loop: true, callback: refresh });
  }

  /**
   * Show an "opponent disconnected — Xs to reconnect" overlay over the
   * opponent half of the screen. Closes when a BATTLE_UPDATE / BATTLE_START
   * arrives (resume) or BATTLE_END (forfeit). Server emits this when the
   * other player drops mid-PvP-battle; it's not surfaced in wild battles.
   */
  private handlePvpDisconnected = (p: PvpDisconnectedPayload): void => {
    if (p.battleId !== this.battleId) return;
    this.openDisconnectOverlay(p.opponentUsername, p.deadline);
  };

  private openDisconnectOverlay(opponentUsername: string, deadline: number): void {
    this.closeDisconnectOverlay();
    this.disconnectDeadline = deadline;
    const W = this.cameras.main.width;
    // Dim the opponent (top-right) half of the screen.
    const dim = this.add
      .rectangle(W / 2, 0, W / 2, 90, 0x000000, 0.55)
      .setOrigin(0, 0)
      .setDepth(40);
    const label = this.add
      .text(W * 0.75, 36, `${opponentUsername}\ndisconnected`, {
        fontFamily: 'monospace',
        fontSize: '9px',
        color: '#ff8866',
        align: 'center',
      })
      .setOrigin(0.5, 0.5)
      .setDepth(41);
    const countdown = this.add
      .text(W * 0.75, 64, '', {
        fontFamily: 'monospace',
        fontSize: '10px',
        color: '#ffe066',
      })
      .setOrigin(0.5, 0.5)
      .setDepth(41);
    this.disconnectOverlay = [dim, label, countdown];
    const refresh = (): void => {
      const remaining = Math.max(0, Math.ceil((this.disconnectDeadline - Date.now()) / 1000));
      countdown.setText(`${remaining}s`);
    };
    refresh();
    this.disconnectTimerEvent?.remove(false);
    this.disconnectTimerEvent = this.time.addEvent({
      delay: 250,
      loop: true,
      callback: refresh,
    });
  }

  private closeDisconnectOverlay(): void {
    for (const obj of this.disconnectOverlay) obj.destroy();
    this.disconnectOverlay = [];
    this.disconnectTimerEvent?.remove(false);
    this.disconnectTimerEvent = null;
  }

  private handleUpdate = (p: BattleUpdatePayload): void => {
    if (p.battleId !== this.battleId && this.battleId !== '') return;
    this.submitWatchdog?.remove(false);
    this.submitWatchdog = null;
    this.closeWaitingOverlay();
    this.closeDisconnectOverlay();
    void this.playEvents(p);
  };

  private handleForceSwitch = (p: BattleForceSwitchPayload): void => {
    this.openForceSwitchModal(p);
  };

  private handleEnd = (p: BattleEndPayload): void => {
    if (p.battleId !== this.battleId && this.battleId !== '') return;
    this.submitWatchdog?.remove(false);
    this.submitWatchdog = null;
    this.endHandled = true;
    this.closeForceSwitchModal();
    this.closeWaitingOverlay();
    this.closeDisconnectOverlay();
    this.clearMenu();
    this.currentMenu = 'message';
    this.game.registry.set('lastBattleEnd', p);
    void this.playEndSequence(p);
  };

  private async playEndSequence(p: BattleEndPayload): Promise<void> {
    if (p.inventory) this.inventory = [...p.inventory];

    const lines: string[] = [];
    if (p.xpGained && p.xpGained > 0) {
      const own = p.party.find((m) => p.levelUps?.[0]?.creatureId === m.creatureId) ?? p.party[0];
      const name = own?.speciesDisplayName ?? 'Your pokémon';
      lines.push(`${name} gained ${p.xpGained} XP!`);
    }
    for (const lu of p.levelUps ?? []) {
      const own = p.party.find((m) => m.creatureId === lu.creatureId);
      const name = own?.speciesDisplayName ?? 'Your pokémon';
      lines.push(`${name} grew to Lv. ${lu.toLevel}!`);
    }
    if (p.outcome === 'player_caught' && p.caughtCreature) {
      const c = p.caughtCreature;
      lines.push(p.message);
      lines.push(`${c.speciesDisplayName} joined party slot ${c.partySlot + 1}!`);
    } else {
      lines.push(p.message);
    }
    for (let i = 0; i < lines.length; i++) {
      const isLast = i === lines.length - 1;
      const text = lines[i] ?? '';
      this.dialogText.setText(isLast ? `${text}\n(press Z to continue)` : text);
      if (!isLast) await this.wait(900);
    }
  }

  private handleError = (err: ErrorPayload): void => {
    this.submitWatchdog?.remove(false);
    this.submitWatchdog = null;
    console.warn('[BattleScene]', err.code, err.message);
    this.flashMessage(`Error: ${err.code}`);
    if (this.currentMenu === 'forceswitch') {
      // Re-show the modal — server still expects a switch.
      this.placeCursor();
    } else if (!this.endHandled) {
      this.openMainMenu();
    }
  };

  /**
   * Play TurnEvents in order. If the update carries a `playerSnapshot`, the
   * left side is re-skinned BEFORE events run so `damage`/`faint` events
   * targeting the new active creature render against the new HP bar.
   */
  private async playEvents(p: BattleUpdatePayload): Promise<void> {
    for (const ev of p.events) {
      if (ev.kind === 'swap') {
        // Apply the visual swap (sprite, info panel, speed label) exactly here,
        // between "come back!" and "Go!" in the event sequence. The snapshot
        // is in the outer payload; which side depends on whose playerId it is.
        if (ev.playerId === this.localPlayerId) {
          if (p.playerSnapshot) {
            this.applyPlayerSwap(p.playerSnapshot);
            this.closeForceSwitchModal();
          }
        } else {
          if (p.opponentSnapshot) this.applyOpponentSwap(p.opponentSnapshot);
        }
        continue;
      }
      if (ev.kind === 'message') {
        this.dialogText.setText(ev.text);
        await this.wait(450);
      } else if (ev.kind === 'damage') {
        const isPlayer = ev.targetCreatureId === this.currentPlayer.creatureId;
        const bar = isPlayer ? this.playerHpFg : this.wildHpFg;
        const oldHp = isPlayer ? this.currentPlayerHp : this.currentWildHp;
        const max = isPlayer ? this.playerMaxHp : this.wildMaxHp;
        const newHp = Math.max(0, oldHp - ev.amount);
        if (isPlayer) this.currentPlayerHp = newHp;
        else this.currentWildHp = newHp;
        if (ev.effectiveness >= 2) this.flashMessage('Super-effective!');
        else if (ev.effectiveness <= 0.5) this.flashMessage('Not very effective…');
        const attacker = isPlayer ? this.wildSprite : this.playerSprite;
        const target = isPlayer ? this.playerSprite : this.wildSprite;
        await this.lungeSprite(attacker, isPlayer ? -10 : 10);
        await Promise.all([this.flashSprite(target), this.shakeSprite(target)]);
        await this.tweenHp(bar, oldHp, newHp, max);
      } else if (ev.kind === 'miss') {
        this.dialogText.setText('Attack missed!');
        await this.wait(350);
      } else if (ev.kind === 'faint') {
        // Mirror to local party cache so the switch menu greys the slot.
        const member = this.partyById.get(ev.creatureId);
        if (member) {
          (member as { fainted: boolean; currentHp: number }).fainted = true;
          (member as { fainted: boolean; currentHp: number }).currentHp = 0;
        }
        const faintedSprite =
          ev.creatureId === this.currentPlayer.creatureId ? this.playerSprite : this.wildSprite;
        await this.faintSprite(faintedSprite);
      } else if (ev.kind === 'escape') {
        this.dialogText.setText(ev.success ? 'Got away safely!' : "Couldn't escape!");
        await this.wait(400);
      } else if (ev.kind === 'catch_attempt') {
        await this.playCatchShakes(ev.shakeCount, ev.success);
      } else if (ev.kind === 'caught') {
        await new Promise<void>((resolve) => {
          this.tweens.add({
            targets: this.wildSprite,
            scaleX: 0,
            scaleY: 0,
            alpha: 0,
            duration: 300,
            onComplete: () => resolve(),
          });
        });
      }
    }
    this.currentPlayerHp = p.playerCurrentHp;
    this.currentWildHp = p.wildCurrentHp;
    this.refreshHp(this.playerHpFg, this.currentPlayerHp, this.playerMaxHp);
    this.refreshHp(this.wildHpFg, this.currentWildHp, this.wildMaxHp);
    if (p.playerInventory) this.inventory = [...p.playerInventory];

    // Sync local party HP for the active creature so the switch menu shows
    // accurate current HP if reopened.
    const active = this.partyById.get(this.currentPlayer.creatureId);
    if (active) (active as { currentHp: number }).currentHp = p.playerCurrentHp;

    // Fallback: if playerSnapshot arrived but no swap event was in the events
    // array (e.g. a future server path that omits it), apply it now so the
    // UI never gets stuck with a stale sprite.
    if (p.playerSnapshot && p.playerSnapshot.creatureId !== this.currentPlayer.creatureId) {
      this.applyPlayerSwap(p.playerSnapshot);
      this.closeForceSwitchModal();
    }

    if (!this.endHandled && this.currentMenu !== 'forceswitch') {
      await this.wait(400);
      this.openMainMenu();
    }
  }

  /** Re-skin the left side for a new active creature: sprite, info text, HP bar. */
  private applyPlayerSwap(snap: BattleParticipantSnapshot): void {
    this.currentPlayer = snap;
    this.playerMaxHp = snap.maxHp;
    this.currentPlayerHp = snap.currentHp;
    this.playerSpeed = snap.speed;
    const newKey = SPRITE_KEYS.creatures.back(snap.speciesId);
    this.playerSprite.setTexture(newKey).setAlpha(1).setY(130).clearTint();
    this.playerInfo.setText(this.formatInfo(snap));
    this.refreshHp(this.playerHpFg, this.currentPlayerHp, this.playerMaxHp);
    this.refreshPvpSpeedLabel();
  }

  /** Re-skin the RIGHT side (opponent/wild) for a newly-switched-in creature. */
  private applyOpponentSwap(snap: BattleParticipantSnapshot): void {
    this.wildMaxHp = snap.maxHp;
    this.currentWildHp = snap.currentHp;
    this.opponentSpeed = snap.speed;
    const newKey = SPRITE_KEYS.creatures.front(snap.speciesId);
    this.wildSprite.setTexture(newKey).setAlpha(1).setY(56).setScale(1).clearTint();
    this.wildInfo.setText(this.formatInfo(snap));
    this.refreshHp(this.wildHpFg, this.currentWildHp, this.wildMaxHp);
    this.refreshPvpSpeedLabel();
  }

  private async playCatchShakes(shakeCount: number, success: boolean): Promise<void> {
    const shakesShown = success ? 4 : Math.max(1, shakeCount);
    for (let i = 0; i < shakesShown; i++) {
      this.dialogText.setText('*' + '* *'.repeat(i) + '*');
      await this.wait(400);
    }
    await this.wait(150);
  }

  // -------------------- Sprite effects --------------------

  private lungeSprite(sprite: Phaser.GameObjects.Image, dx: number): Promise<void> {
    const ox = sprite.x;
    return new Promise((resolve) => {
      this.tweens.add({
        targets: sprite,
        x: ox + dx,
        duration: 80,
        yoyo: true,
        onComplete: () => {
          sprite.x = ox;
          resolve();
        },
      });
    });
  }

  private flashSprite(sprite: Phaser.GameObjects.Image): Promise<void> {
    return new Promise((resolve) => {
      sprite.setTint(0xffffff);
      this.time.delayedCall(80, () => {
        sprite.clearTint();
        this.time.delayedCall(40, () => {
          sprite.setTint(0xffffff);
          this.time.delayedCall(80, () => {
            sprite.clearTint();
            resolve();
          });
        });
      });
    });
  }

  private shakeSprite(sprite: Phaser.GameObjects.Image): Promise<void> {
    const ox = sprite.x;
    return new Promise((resolve) => {
      this.tweens.add({
        targets: sprite,
        x: ox + 3,
        duration: 50,
        yoyo: true,
        repeat: 1,
        onComplete: () => {
          sprite.x = ox;
          resolve();
        },
      });
    });
  }

  private faintSprite(sprite: Phaser.GameObjects.Image): Promise<void> {
    return new Promise((resolve) => {
      this.tweens.add({
        targets: sprite,
        alpha: 0,
        y: sprite.y + 12,
        duration: 350,
        onComplete: () => resolve(),
      });
    });
  }

  private wait(ms: number): Promise<void> {
    return new Promise((resolve) => this.time.delayedCall(ms, resolve));
  }

  // -------------------- Exit --------------------

  private exitToOverworld(): void {
    this.scene.stop();
    this.scene.run('OverworldScene');
    const overworld = this.scene.get('OverworldScene') as
      | (Phaser.Scene & { exitBattle?: () => void })
      | undefined;
    overworld?.exitBattle?.();
  }
}
